import { TFunction } from 'i18next';

import { KnowledgeDynamicToolParameter, PreviewKnowledgeDynamicToolResult } from '../types/Knowledge';

type PreviewGroup = {
  value?: unknown;
  count?: unknown;
};

type PreviewParameterLike = Pick<
  KnowledgeDynamicToolParameter,
  'name' | 'defaultValue' | 'example' | 'matchMode' | 'isRequired'
>;

const UNARY_MATCH_MODES = new Set(['exists', 'isempty']);

export const isUnaryMatchMode = (matchMode: string | undefined): boolean =>
  UNARY_MATCH_MODES.has((matchMode ?? '').trim().toLowerCase());

export const mergePreviewArgumentsForExecution = (
  parameters: PreviewParameterLike[],
  previewArguments: Record<string, string>,
): Record<string, string> => {
  const merged: Record<string, string> = { ...previewArguments };

  for (const parameter of parameters) {
    const name = parameter.name.trim();
    if (!name || isUnaryMatchMode(parameter.matchMode)) {
      continue;
    }

    const currentValue = merged[name]?.trim() ?? '';
    if (!currentValue && parameter.defaultValue?.trim()) {
      merged[name] = parameter.defaultValue.trim();
    }
  }

  return merged;
};

export const syncPreviewArguments = (
  parameters: PreviewParameterLike[],
  current: Record<string, string>,
): Record<string, string> => {
  const next: Record<string, string> = {};
  let changed = false;

  for (const parameter of parameters) {
    const name = parameter.name.trim();
    if (!name) {
      continue;
    }

    const existingValue = current[name]?.trim() ?? '';
    const defaultValue = parameter.defaultValue?.trim() ?? '';

    if (existingValue) {
      next[name] = current[name] ?? '';
    } else if (defaultValue) {
      next[name] = defaultValue;
      if (current[name] !== defaultValue) {
        changed = true;
      }
    } else {
      next[name] = '';
      if (current[name] !== '') {
        changed = true;
      }
    }

    if (!(name in current)) {
      changed = true;
    }
  }

  if (Object.keys(current).length !== Object.keys(next).length) {
    changed = true;
  }

  return changed ? next : current;
};

const formatGroupPreview = (groups: PreviewGroup[]): string =>  groups
    .slice(0, 3)
    .map(group => `${String(group.value ?? '')} (${Number(group.count ?? 0)})`)
    .join(', ');

export const buildDynamicToolPreviewSummary = (
  preview: PreviewKnowledgeDynamicToolResult,
  t: TFunction,
): string => {
  const title = preview.documentTitle ?? '';
  const matched = preview.matchedCount ?? 0;
  const mode = (preview.resultMode ?? 'list').toLowerCase();

  if (mode === 'count') {
    return t('dynamicTools.previewSummaryCount', {
      ns: 'knowledge',
      matched,
      title,
    });
  }

  if (mode === 'groupby') {
    const groups = Array.isArray(preview.groups) ? preview.groups as PreviewGroup[] : [];
    return t('dynamicTools.previewSummaryGroupBy', {
      ns: 'knowledge',
      matched,
      groups: preview.count ?? groups.length,
      path: preview.groupByJsonPath ?? '',
      title,
      topGroups: formatGroupPreview(groups),
    });
  }

  const parts = [
    preview.isSynthetic
      ? t('dynamicTools.previewSummarySynthetic', { ns: 'knowledge' })
      : null,
    t('dynamicTools.previewSummary', {
      ns: 'knowledge',
      matched,
      returned: preview.count ?? 0,
      title,
    }),
  ].filter((part): part is string => Boolean(part));

  if (Array.isArray(preview.relatedKnowledge) && preview.relatedKnowledge.length > 0) {
    parts.push(t('dynamicTools.previewSummaryHybrid', {
      ns: 'knowledge',
      count: preview.relatedKnowledge.length,
    }));
  }

  if (preview.truncated) {
    parts.push(t('dynamicTools.previewSummaryTruncated', {
      ns: 'knowledge',
      maxResults: preview.maxResults ?? 0,
    }));
  }

  return parts.join(' ');
};

export const getDynamicToolPreviewGroups = (
  preview: PreviewKnowledgeDynamicToolResult,
): PreviewGroup[] => {
  if ((preview.resultMode ?? 'list').toLowerCase() !== 'groupby') {
    return [];
  }

  return Array.isArray(preview.groups) ? preview.groups as PreviewGroup[] : [];
};
